import { yupResolver } from '@hookform/resolvers/yup';
import Add from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import TextInput from '~components/FormFields/v2/TextInput';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { Order } from '~hooks/useOrders/models';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import { useStores } from '~store/RootStore';
import { alert, AlertTypes } from '~types/AlertTypes';

interface RejectOrderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  order?: Order;
}

const REJECT_REASON_OPTIONS = [
  { label: 'Not enough material', value: 'not_enough_material' },
  { label: 'Not enough trucks', value: 'not_enough_trucks' },
  { label: 'Wrong start time', value: 'wrong_start_time' },
  { label: 'Wrong material', value: 'wrong_material' },
  { label: 'Weather', value: 'weather' },
];
const MAX_REJECT_REASON_LENGTH = 100;

const rejectOrderSchema = yup.object().shape({
  rejectReason: yup.string().notRequired(),
});
type RejectOrderDTO = yup.InferType<typeof rejectOrderSchema>;

const RejectOrderDialog = ({ isOpen, onClose, order }: RejectOrderDialogProps) => {
  const { toasterStore } = useStores();
  const { rejectOrder } = useOrdersDispatch();

  const orderId = order?.id;
  const [isRejectingOrder, setIsRejectingOrder] = useState(false);

  const form = useForm<RejectOrderDTO>({
    resolver: yupResolver(rejectOrderSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      rejectReason: '',
    },
  });

  const { control, watch } = form;
  const watchRejectReason = watch('rejectReason');

  const handleSubmit = useCallback(() => {
    form.handleSubmit(async (data) => {
      if (!orderId) {
        return;
      }

      try {
        setIsRejectingOrder(true);
        const rejectedOrder = await rejectOrder(orderId, data.rejectReason || undefined);
        if (rejectedOrder) {
          toasterStore.push(
            alert(
              t('dispatch.order.rejected', { name: rejectedOrder.orderId }),
              AlertTypes.success,
            ),
          );
        }
        onClose();
        form.reset();
      } catch {
        // Error is handled by the hook
      } finally {
        setIsRejectingOrder(false);
      }
    })();
  }, [form.handleSubmit, orderId]);

  const handleClose = useCallback(() => {
    if (!isRejectingOrder) {
      onClose();
      form.reset();
    }
  }, [isRejectingOrder, onClose]);

  const handleQuickAdd = useCallback(
    (value: string) => {
      const currentValue = watchRejectReason?.trim() || '';
      const separator = currentValue ? ' ' : '';
      const newValue = `${currentValue}${separator}${value}`.slice(
        0,
        MAX_REJECT_REASON_LENGTH,
      );
      form.setValue('rejectReason', newValue);
    },
    [watchRejectReason],
  );

  return (
    <Dialog isOpen={isOpen} onClose={handleClose}>
      <DialogHeader onClose={handleClose}>
        {t('dispatch.reject_order.title')}
      </DialogHeader>

      <DialogContent>
        <FormProvider {...form}>
          <Typography>{t('dispatch.reject_order.description')}</Typography>

          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Typography component="span" variant="subtitle2" sx={{ width: '100%' }}>
              {t('dispatch.reject_order.quick_add')}
            </Typography>

            {REJECT_REASON_OPTIONS.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                icon={<Add />}
                onClick={() => handleQuickAdd(option.label)}
                sx={{ minWidth: 'fit-content' }}
                data-ui={`page-dispatch__reject-order__order-${order?.orderId}__button`}
              />
            ))}
          </Box>

          <Box sx={{ mt: 2 }}>
            <TextInput
              control={control}
              dataUi="page-dispatch__reject-order-dialog__reject-reason-input"
              slotProps={{ htmlInput: { maxLength: MAX_REJECT_REASON_LENGTH } }}
              label={t('dispatch.reject_order.reject_reason')}
              name="rejectReason"
              rows={3}
            />
            <Typography variant="caption">
              {t('dispatch.reject_order.character_limit', {
                count: Math.max(
                  0,
                  MAX_REJECT_REASON_LENGTH - (watchRejectReason?.length ?? 0),
                ),
              })}
            </Typography>
          </Box>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isRejectingOrder}
          onClick={handleClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          color="brandV2Red"
          disabled={isRejectingOrder}
          loading={isRejectingOrder}
          onClick={handleSubmit}
        >
          {t('actions.reject')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RejectOrderDialog;
